import {
  CHAVE_DIAGNOSTICO,
  CHAVE_SESSAO,
  NOME_ARMAZEM,
  NOME_BANCO,
  VERSAO_SCHEMA,
  type SessaoSalva,
} from './schema';

/**
 * Persistência em IndexedDB (§12).
 *
 * Regra de ouro de §12 e §17: **falha de persistência não interrompe a partida**.
 * Toda operação resolve, nunca rejeita; quem chama recebe `ok: false` e mostra
 * um aviso discreto, sem modal.
 */

export interface ResultadoEscrita {
  readonly ok: boolean;
  readonly erro?: string;
}

export interface Armazem {
  /**
   * Atalhos para a **sessão em cadeia**, e só para ela.
   *
   * Cada modo grava na sua chave (`chaveDaPartida`, ADR-0009), e estes três
   * falam sempre com `CHAVE_SESSAO`. É de propósito: `limpar` é o que a troca
   * de modo dispara (§22.5), e apagar a cadeia não pode levar junto a partida
   * do dia. Quem precisa da chave de um modo qualquer usa `ler` e `guardar`.
   */
  carregar(): Promise<unknown>;
  salvar(sessao: SessaoSalva): Promise<ResultadoEscrita>;
  /** Remove só a sessão em cadeia (T09-05, T09-06). */
  limpar(): Promise<ResultadoEscrita>;
  /** Guarda a cópia bruta de uma sessão incompatível para diagnóstico (§17). */
  guardarDiagnostico(bruto: unknown): Promise<ResultadoEscrita>;
  /**
   * Leitura e escrita de qualquer chave do mesmo armazém.
   *
   * É o que a partida de cada modo e o modo em curso usam. Falhar em ler devolve
   * `undefined` e falhar em gravar devolve o erro — §12 manda que nenhum dos
   * dois interrompa a partida.
   */
  ler(chave: string): Promise<unknown>;
  guardar(chave: string, valor: unknown): Promise<ResultadoEscrita>;
  remover(chave: string): Promise<ResultadoEscrita>;
}

function mensagemDeErro(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro);
}

function promessaDeRequisicao<T>(requisicao: IDBRequest<T>): Promise<T> {
  return new Promise((resolver, rejeitar) => {
    requisicao.onsuccess = () => resolver(requisicao.result);
    requisicao.onerror = () => rejeitar(requisicao.error ?? new Error('IndexedDB falhou'));
  });
}

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolver, rejeitar) => {
    const requisicao = indexedDB.open(NOME_BANCO, VERSAO_SCHEMA);
    requisicao.onupgradeneeded = () => {
      const banco = requisicao.result;
      if (!banco.objectStoreNames.contains(NOME_ARMAZEM)) {
        banco.createObjectStore(NOME_ARMAZEM);
      }
    };
    requisicao.onsuccess = () => resolver(requisicao.result);
    requisicao.onerror = () => rejeitar(requisicao.error ?? new Error('IndexedDB indisponível'));
    requisicao.onblocked = () => rejeitar(new Error('IndexedDB bloqueado por outra aba'));
  });
}

async function escrever(chave: string, valor: unknown): Promise<ResultadoEscrita> {
  try {
    const banco = await abrir();
    try {
      const transacao = banco.transaction(NOME_ARMAZEM, 'readwrite');
      await promessaDeRequisicao(transacao.objectStore(NOME_ARMAZEM).put(valor, chave));
      return { ok: true };
    } finally {
      banco.close();
    }
  } catch (erro) {
    return { ok: false, erro: mensagemDeErro(erro) };
  }
}

export function criarArmazem(): Armazem {
  return {
    async ler(chave) {
      try {
        const banco = await abrir();
        try {
          const transacao = banco.transaction(NOME_ARMAZEM, 'readonly');
          return await promessaDeRequisicao(transacao.objectStore(NOME_ARMAZEM).get(chave));
        } finally {
          banco.close();
        }
      } catch {
        // Sem banco disponível o jogo simplesmente começa novo (§12).
        return undefined;
      }
    },

    carregar() {
      return this.ler(CHAVE_SESSAO);
    },

    guardar(chave, valor) {
      return escrever(chave, valor);
    },

    async remover(chave) {
      try {
        const banco = await abrir();
        try {
          const transacao = banco.transaction(NOME_ARMAZEM, 'readwrite');
          await promessaDeRequisicao(transacao.objectStore(NOME_ARMAZEM).delete(chave));
          return { ok: true };
        } finally {
          banco.close();
        }
      } catch (erro) {
        return { ok: false, erro: mensagemDeErro(erro) };
      }
    },

    salvar(sessao) {
      return escrever(CHAVE_SESSAO, sessao);
    },

    guardarDiagnostico(bruto) {
      return escrever(CHAVE_DIAGNOSTICO, bruto);
    },

    limpar() {
      return this.remover(CHAVE_SESSAO);
    },
  };
}

/** Armazém que não guarda nada. Usado quando o IndexedDB é inacessível. */
export const ARMAZEM_NULO: Armazem = {
  carregar: () => Promise.resolve(undefined),
  salvar: () => Promise.resolve({ ok: false, erro: 'armazém indisponível' }),
  guardarDiagnostico: () => Promise.resolve({ ok: false, erro: 'armazém indisponível' }),
  limpar: () => Promise.resolve({ ok: true }),
  ler: () => Promise.resolve(undefined),
  guardar: () => Promise.resolve({ ok: false, erro: 'armazém indisponível' }),
  remover: () => Promise.resolve({ ok: true }),
};
